import React from 'react';
import PropTypes from 'prop-types';
import { styled } from '@mui/material/styles';
import FormControl from '@mui/material/FormControl';
import FormHelperText from '@mui/material/FormHelperText';
import Input from '@mui/material/Input';
import InputLabel from '@mui/material/InputLabel';
import Check from '@mui/icons-material/Check';
import { uuidv4 } from '../../helpers/helpers';
import { styles, decimalStyles, feedbackDecimalStyles } from './style/customInputStyle';
import { dangerColor, successColor } from '../../assets/styles/jss/main';

const StyledFormControl = styled(FormControl, {
  shouldForwardProp: (prop) => !['hasAdornment', 'hasLabel'].includes(prop),
})(({ hasAdornment, hasLabel }) => ({
  ...styles.formControl,
  ...(!hasLabel && hasAdornment && styles.inputWithAdornment),
}));

const StyledInputLabel = styled(InputLabel)({
  ...styles.labelRoot,
  whiteSpace: 'normal',
});

const StyledFormHelperText = styled(FormHelperText)({
  ...styles.helpText,
});

const StyledInput = styled(Input, {
  shouldForwardProp: (prop) =>
    !['hasError', 'hasSuccess', 'transparent', 'white', 'nbDecimal', 'isClickable', 'hasMultiline'].includes(prop),
})(({ hasError, hasSuccess, transparent, white, nbDecimal, isClickable, hasMultiline }) => ({
  '&.MuiInput-root': {
    ...styles.root,
    overflow: 'hidden',
    ...(nbDecimal > 0 && decimalStyles[nbDecimal]),
    ...(hasMultiline && {
      border: hasError ? `2px solid ${dangerColor}` : hasSuccess ? `2px solid ${successColor}` : '2px solid #ececee',
      borderRadius: '5px',
      background: '#f6f6f9',
    }),
  },
  '& .MuiInput-input': {
    ...styles.input,
    ...(hasError && !hasSuccess && styles.inputError),
    ...(hasSuccess && !hasError && styles.inputSuccess),
    ...(transparent && styles.transparent),
    ...(white && styles.white),
    ...(isClickable && styles.clickable),
    ...(hasMultiline && { border: 'none', background: 'transparent' }),
  },
  '&.Mui-disabled': styles.disabled,
}));

const StyledCheck = styled(Check, {
  shouldForwardProp: (prop) => !['hasLabel', 'hasEndAdornment', 'nbDecimal', 'hasHelp'].includes(prop),
})(({ hasLabel, hasEndAdornment, nbDecimal, hasHelp }) => ({
  ...styles.feedback,
  ...styles.labelRootSuccess,
  ...(!hasLabel && styles.feedbackNoLabel),
  ...(hasEndAdornment && styles.feedbackAdornment),
  ...(nbDecimal > 0 && feedbackDecimalStyles[nbDecimal]),
  ...(hasHelp && styles.feedbackWithHelp),
}));

const CustomInput = ({
  formControlProps = { fullWidth: true },
  labelText,
  id = uuidv4(),
  labelProps,
  inputProps,
  error,
  success,
  helpText,
  value,
  type = 'text',
  nbDecimal = 0,
  transparent,
  white,
}) => {
  const hasAdornment = inputProps?.startAdornment !== undefined || inputProps?.endAdornment !== undefined;
  const hasEndAdornment = inputProps?.endAdornment !== undefined;
  const isClickable = !!(inputProps?.readOnly && inputProps?.onClick);
  const hasMultiline = !!inputProps?.multiline;

  return (
    <StyledFormControl
      {...formControlProps}
      hasAdornment={hasAdornment}
      hasLabel={labelText !== undefined}
      aria-describedby={`${id}-text`}>
      {labelText !== undefined && (
        <StyledInputLabel shrink htmlFor={id} {...labelProps}>
          {labelText}
        </StyledInputLabel>
      )}

      {helpText && <StyledFormHelperText id={`${id}-text`}>{helpText}</StyledFormHelperText>}

      <StyledInput
        disableUnderline
        id={id}
        value={value}
        type={type}
        hasError={!!error}
        hasSuccess={!!success}
        transparent={transparent}
        white={white}
        nbDecimal={nbDecimal}
        isClickable={isClickable}
        hasMultiline={hasMultiline}
        {...inputProps}
      />

      {success && (
        <StyledCheck
          hasLabel={labelText !== undefined}
          hasEndAdornment={hasEndAdornment}
          nbDecimal={nbDecimal}
          hasHelp={helpText !== undefined}
        />
      )}
    </StyledFormControl>
  );
};

CustomInput.propTypes = {
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  labelText: PropTypes.node,
  labelProps: PropTypes.object,
  id: PropTypes.string,
  inputProps: PropTypes.object,
  formControlProps: PropTypes.object,
  error: PropTypes.bool,
  success: PropTypes.bool,
  nbDecimal: PropTypes.oneOf([0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10]),
  helpText: PropTypes.oneOfType([PropTypes.string, PropTypes.node, PropTypes.bool]),
  transparent: PropTypes.bool,
  white: PropTypes.bool,
};

export default CustomInput;
